import { useEffect, useState } from 'react';
import { router } from '@inertiajs/react';
import { cn } from '../../lib/cn';

export default function InviteIsland({ invite, onDismiss }) {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (invite) {
      setIsExiting(false);
      const t = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(t);
    }
    if (isVisible) {
      setIsExiting(true);
      const t = setTimeout(() => {
        setIsVisible(false);
        setIsExiting(false);
      }, 300);
      return () => clearTimeout(t);
    }
  }, [invite, isVisible]);

  if (!invite && !isVisible) return null;

  const handleAccept = () => {
    if (!invite) return;
    router.visit(invite.join_url || `/student/live_sessions/${invite.join_code}`);
    onDismiss?.();
  };

  return (
    <div
      className={cn(
        'fixed top-4 left-1/2 -translate-x-1/2 z-50',
        'transition-all duration-300 ease-out',
        isExiting ? 'opacity-0 -translate-y-2 scale-95' : 'opacity-100 translate-y-0 scale-100',
        !isVisible && !isExiting && 'opacity-0 -translate-y-4 scale-90'
      )}
    >
      <div
        className={cn(
          'invite-island-glow',
          'flex items-center gap-3 pl-4 pr-2 py-2',
          'bg-gray-900 text-white rounded-full',
          'dark:bg-white dark:text-gray-900',
          'border border-blue-400/40 dark:border-blue-400/60'
        )}
      >
        <span
          className="w-2 h-2 rounded-full bg-green-400 dark:bg-green-500 flex-shrink-0"
          style={{ animation: 'pulse-dot 1.5s ease-in-out infinite' }}
        />
        <div className="flex flex-col leading-tight min-w-0">
          <span className="text-sm font-medium whitespace-nowrap">
            {invite?.teacher_name ? `${invite.teacher_name} invited you` : 'You’ve been invited'}
          </span>
          <span className="text-xs opacity-70 whitespace-nowrap">
            {labelForView(invite?.view)}
          </span>
        </div>
        <div className="flex items-center gap-1 ml-2">
          <button
            onClick={onDismiss}
            className={cn(
              'px-3 py-1.5 text-xs rounded-full',
              'text-white/70 hover:text-white hover:bg-white/10',
              'dark:text-gray-900/70 dark:hover:text-gray-900 dark:hover:bg-gray-900/10',
              'transition-colors'
            )}
          >
            Dismiss
          </button>
          <button
            onClick={handleAccept}
            className={cn(
              'px-3 py-1.5 text-xs font-semibold rounded-full',
              'bg-blue-500 text-white hover:bg-blue-400',
              'dark:bg-blue-500 dark:text-white dark:hover:bg-blue-400',
              'transition-colors'
            )}
          >
            Join
          </button>
        </div>
      </div>

      <style>{`
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.8); }
        }
        @keyframes invite-island-glow {
          0%, 100% {
            box-shadow:
              0 0 0 1px rgba(59, 130, 246, 0.45),
              0 0 14px 2px rgba(59, 130, 246, 0.45),
              0 8px 24px rgba(0, 0, 0, 0.25);
          }
          50% {
            box-shadow:
              0 0 0 2px rgba(59, 130, 246, 0.65),
              0 0 28px 8px rgba(59, 130, 246, 0.7),
              0 8px 24px rgba(0, 0, 0, 0.25);
          }
        }
        .invite-island-glow {
          animation: invite-island-glow 1.8s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .invite-island-glow {
            animation: none;
            box-shadow:
              0 0 0 1px rgba(59, 130, 246, 0.5),
              0 0 18px 4px rgba(59, 130, 246, 0.5),
              0 8px 24px rgba(0, 0, 0, 0.25);
          }
        }
      `}</style>
    </div>
  );
}

function labelForView(view) {
  if (view === 'VocabularyPractice') return 'Vocabulary session';
  if (view === 'ChapterReader') return 'Reading session';
  if (view === 'SentenceStudio') return 'Sentence Studio';
  if (view === 'CloseReading') return 'Close reading';
  return 'Live session';
}
